import React, { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { Check, Package, Truck } from "lucide-react";
import apiClient from "../lib/api";
import { useI18n } from "../i18n";

const OrderSuccess = () => {
  const { t, lang } = useI18n();
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const orderId = params.get("order_id");
  const tracking = params.get("tracking");
  const mocked = params.get("mocked");
  const isMollie = params.get("mollie") === "1";
  const [status, setStatus] = useState(sessionId || isMollie ? "checking" : "ok");
  const [orderInfo, setOrderInfo] = useState(null);

  useEffect(() => {
    if (sessionId) {
      let attempts = 0;
      const poll = async () => {
        try {
          const { data } = await apiClient.get(`/checkout/status/${sessionId}`);
          if (data.payment_status === "paid") { setStatus("paid"); setOrderInfo(data); return; }
          if (data.status === "expired") { setStatus("expired"); return; }
          if (attempts >= 10) { setStatus("pending"); return; }
          attempts += 1;
          setTimeout(poll, 2000);
        } catch { setStatus("error"); }
      };
      poll();
      return;
    }
    if (isMollie && orderId) {
      let attempts = 0;
      const pollMollie = async () => {
        try {
          const { data } = await apiClient.get(`/checkout/mollie/status/${orderId}`);
          if (data.payment_status === "paid") { setStatus("paid"); setOrderInfo({ order_id: orderId, tracking_code: data.tracking_code }); return; }
          if (data.payment_status === "failed" || data.payment_status === "expired") { setStatus(data.payment_status); return; }
          if (attempts >= 10) { setStatus("pending"); return; }
          attempts += 1;
          setTimeout(pollMollie, 2000);
        } catch { setStatus("error"); }
      };
      pollMollie();
    }
  }, [sessionId, isMollie, orderId]);

  return (
    <div className="max-w-[800px] mx-auto px-6 py-24 text-center" data-testid="order-success-page">
      <div className="w-20 h-20 mx-auto bg-olive/10 flex items-center justify-center rounded-full mb-6">
        <Check className="w-9 h-9 text-olive" />
      </div>
      <h1 className="font-display text-5xl tracking-tighter font-light">
        {status === "checking" ? (lang === "nl" ? "Controleren…" : "Verifying…") : (lang === "nl" ? "Bedankt!" : "Thank you!")}
      </h1>
      {mocked === "1" && <div className="mt-3 inline-block bg-terracotta/10 text-terracotta font-label px-3 py-1.5">MOCKED PAYMENT — DEMO</div>}
      <p className="mt-4 text-ink-muted">
        {status === "paid" ? (lang === "nl" ? "Je betaling is ontvangen." : "Your payment has been received.") :
         status === "checking" ? (lang === "nl" ? "We bevestigen je betaling." : "We are confirming your payment.") :
         status === "pending" ? (lang === "nl" ? "Betaling in afwachting. Je ontvangt een email zodra deze is bevestigd." : "Payment is pending. You will receive an email once confirmed.") :
         status === "expired" ? (lang === "nl" ? "Sessie verlopen." : "Session expired.") :
         status === "failed" ? (lang === "nl" ? "Betaling mislukt of geannuleerd." : "Payment failed or was cancelled.") :
         (lang === "nl" ? "Je bestelling is geplaatst." : "Your order has been placed.")}
      </p>

      <div className="mt-10 bg-white border border-black/10 p-6 text-left">
        <div className="flex items-center justify-between border-b border-black/10 pb-4">
          <div>
            <div className="font-label text-ink-muted">Order</div>
            <div className="font-display text-xl">{orderId || orderInfo?.order_id}</div>
          </div>
          {tracking && (
            <div>
              <div className="font-label text-ink-muted">Tracking</div>
              <div className="font-display text-xl">{tracking}</div>
            </div>
          )}
        </div>
        <div className="mt-5 grid grid-cols-2 gap-4 text-sm">
          <div className="flex items-center gap-2"><Package className="w-4 h-4 text-terracotta" /> {lang === "nl" ? "Voorbereiden" : "Preparing"}</div>
          <div className="flex items-center gap-2"><Truck className="w-4 h-4 text-terracotta" /> PostNL / DHL</div>
        </div>
      </div>

      <div className="mt-8 flex justify-center gap-3">
        {tracking && <Link to={`/track?code=${tracking}`} className="btn-secondary text-sm" data-testid="track-from-success">{t("nav_track")}</Link>}
        <Link to="/shop" className="btn-primary text-sm">{t("continue_shopping")}</Link>
      </div>
    </div>
  );
};

export default OrderSuccess;
